import React, { useState } from 'react';
import {
  CATEGORIES,
  CATEGORY_BRANDS,
  CATEGORY_BRAND_MODELS,
  PRIORITIES,
} from './postPublishUpsellData';
import css from './PostPublishUpsell.module.css';

/**
 * PostPublishUpsell
 *
 * Shown once to a seller right after their listing goes live. Congratulates
 * them, then walks a short preference-discovery flow: next-vehicle category →
 * brand → model → what matters most. Sunny learns the customer — never asks
 * "what payment do you want".
 *
 * Steps: category → brand → model (only when we have model data) → priorities → done
 * Every step is skippable and the whole card is dismissible.
 *
 * Props:
 *   sellerName — first name if known (optional)
 *   onComplete — ({ category, brand, model, priorities }) => void, fired once at the end
 *   onDismiss  — () => void, seller closed it without finishing
 */
const PostPublishUpsell = ({ sellerName, onComplete, onDismiss }) => {
  const [step, setStep] = useState('category'); // category | brand | model | priorities | done
  const [selection, setSelection] = useState({});
  const [priorities, setPriorities] = useState([]);

  const pickCategory = cat => {
    if (cat.key === 'unsure') {
      setSelection({ category: 'unsure' });
      setStep('priorities');
      return;
    }
    setSelection({ category: cat.key, categoryLabel: cat.label });
    setStep('brand');
  };

  const pickBrand = brand => {
    const next = { ...selection, brand };
    setSelection(next);
    const models = brand !== 'No Preference' && CATEGORY_BRAND_MODELS[selection.category]?.[brand];
    setStep(models?.length ? 'model' : 'priorities');
  };

  const pickModel = model => {
    setSelection(prev => ({ ...prev, model }));
    setStep('priorities');
  };

  const togglePriority = key => {
    setPriorities(prev => (prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]));
  };

  const finish = () => {
    setStep('done');
    onComplete?.({ ...selection, priorities });
  };

  const brands = CATEGORY_BRANDS[selection.category] || [];
  const models =
    (selection.brand && CATEGORY_BRAND_MODELS[selection.category]?.[selection.brand]) || [];

  return (
    <div className={css.root} role="dialog" aria-label="Your listing is live">
      <button className={css.close} onClick={onDismiss} aria-label="Close">
        ×
      </button>

      <div className={css.header}>
        <div className={css.confetti} aria-hidden="true">
          🎉
        </div>
        <h3 className={css.title}>Congratulations{sellerName ? `, ${sellerName}` : ''}!</h3>
        <p className={css.sub}>
          Your vehicle is now live on SkipTheDealerships.ai. We&apos;ll begin matching verified
          buyers immediately.
        </p>
      </div>

      {step === 'category' && (
        <>
          <p className={css.question}>
            While we work on selling your vehicle — what would you like to drive next?
          </p>
          <div className={css.grid}>
            {CATEGORIES.map(cat => (
              <button key={cat.key} className={css.option} onClick={() => pickCategory(cat)}>
                <span className={css.optionIcon} aria-hidden="true">
                  {cat.icon}
                </span>
                {cat.label}
              </button>
            ))}
          </div>
        </>
      )}

      {step === 'brand' && (
        <>
          <p className={css.question}>Great choice. Do you already have a brand in mind?</p>
          <div className={css.grid}>
            {brands.map(b => (
              <button key={b} className={css.option} onClick={() => pickBrand(b)}>
                {b}
              </button>
            ))}
            <button className={css.optionMuted} onClick={() => pickBrand('No Preference')}>
              No Preference
            </button>
          </div>
        </>
      )}

      {step === 'model' && (
        <>
          <p className={css.question}>Perfect. Which model interests you?</p>
          <div className={css.grid}>
            {models.map(m => (
              <button key={m} className={css.option} onClick={() => pickModel(m)}>
                {m}
              </button>
            ))}
            <button className={css.optionMuted} onClick={() => pickModel('Not sure')}>
              Not sure
            </button>
          </div>
        </>
      )}

      {step === 'priorities' && (
        <>
          <p className={css.question}>What matters most? (pick as many as you like)</p>
          <div className={css.grid}>
            {PRIORITIES.map(p => (
              <button
                key={p.key}
                className={priorities.includes(p.key) ? css.optionSelected : css.option}
                onClick={() => togglePriority(p.key)}
                aria-pressed={priorities.includes(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button className={css.doneBtn} onClick={finish}>
            {priorities.length ? 'Done — find my match' : 'Skip for now'}
          </button>
        </>
      )}

      {step === 'done' && (
        <p className={css.thanks}>
          Perfect — we&apos;ll keep an eye out and let you know when we spot a great match. 🚗
        </p>
      )}
    </div>
  );
};

export default PostPublishUpsell;
